interface BestMatch {
    index: number;
    difference: number;
}

// returns the index of an item that matches currentTimeMs.  Notes:
//    1. if there is more than 1 match only the first one found will be returned (because this is binary search
//         it may not be the first in the list)
//    2. if there is no exact match, the closest item that comes before currentTimeMs will be returned
export function binarySearch<T extends { startTimeMs: number }>(
    sortedItems: T[],
    currentTimeMs: number,
): number | undefined {
    if (sortedItems.length === 0) {
        return undefined;
    }

    // if all items in the list start after the current time, return 0 (should not happen in practice)
    if (sortedItems[0].startTimeMs > currentTimeMs) {
        return 0;
    }

    // if all items in the list start before the current time, return length - 1
    if (sortedItems[sortedItems.length - 1].startTimeMs <= currentTimeMs) {
        return sortedItems.length - 1;
    }

    let lowIndex = 0;
    let highIndex = sortedItems.length - 1;
    const bestMatch: BestMatch = {
        index: 0,
        difference: currentTimeMs - sortedItems[0].startTimeMs,
    };
    while (lowIndex < highIndex) {
        const midIndex = lowIndex + Math.floor((highIndex - lowIndex) / 2);
        const difference = currentTimeMs - sortedItems[midIndex].startTimeMs;

        // found a match, return it.
        //       Note: this prioritizes speed over predictability.  If there are > 1 item
        //            with that matches currentTimeMs, it may choose the index of any of
        //            the matching item, instead of always returning the first or last.
        if (difference === 0) {
            return midIndex;
        }

        // found a better match because it's before currentTimeMs, but closer to it
        if (difference > 0 && difference < bestMatch.difference) {
            bestMatch.index = midIndex;
            bestMatch.difference = difference;
        }

        if (difference > 0) {
            // if the current item is less than currentTimeMs, search the upper half
            lowIndex = midIndex + 1;
        } else {
            // if the current item is greater than currentTimeMs, search the lower half
            highIndex = midIndex;
        }
    }

    return bestMatch.index;
}
